import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { SensoryProvider } from "./contexts/SensoryContext";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { Navbar } from "./components/layout/Navbar";
import { Footer } from "./components/layout/Footer";
import { ProtectedRoute } from "./components/ProtectedRoute";
import { ScrollToTop } from "./components/ScrollToTop";

// Pages
import LandingPage from "./pages/LandingPage";
import Dashboard from "./pages/Dashboard";
import Login from "./pages/Auth/Login";
import Register from "./pages/Auth/Register";
import ForgotPassword from "./pages/Auth/ForgotPassword";
import About from "./pages/About";
import Learn from "./pages/Learn";
import Directory from "./pages/Directory";
import Resources from "./pages/Resources";
import Community from "./pages/Community";
import Events from "./pages/Events";
import Training from "./pages/Training";
import Volunteer from "./pages/Volunteer";
import FAQs from "./pages/FAQs";
import AdminPanel from "./pages/AdminPanel";

// Root component that handles auth-based routing
const AppRoutes = () => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col overflow-x-hidden">
      <ScrollToTop />
      <Navbar />
      <main className="flex-1">
        <Routes>
          {/* Public routes */}
          <Route 
            path="/" 
            element={isAuthenticated ? <Navigate to="/dashboard" replace /> : <LandingPage />} 
          />
          <Route 
            path="/login" 
            element={isAuthenticated ? <Navigate to="/dashboard" replace /> : <Login />} 
          />
          <Route 
            path="/register" 
            element={isAuthenticated ? <Navigate to="/dashboard" replace /> : <Register />} 
          />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          
          {/* Protected routes - require authentication */}
          <Route path="/dashboard" element={
            <ProtectedRoute><Dashboard /></ProtectedRoute>
          } />
          <Route path="/about" element={
            <ProtectedRoute><About /></ProtectedRoute>
          } />
          <Route path="/learn" element={
            <ProtectedRoute><Learn /></ProtectedRoute>
          } />
          <Route path="/learn/*" element={
            <ProtectedRoute><Learn /></ProtectedRoute>
          } />
          <Route path="/directory" element={
            <ProtectedRoute><Directory /></ProtectedRoute>
          } />
          <Route path="/resources" element={
            <ProtectedRoute><Resources /></ProtectedRoute>
          } />
          <Route path="/community" element={
            <ProtectedRoute><Community /></ProtectedRoute>
          } />
          <Route path="/events" element={
            <ProtectedRoute><Events /></ProtectedRoute>
          } />
          <Route path="/training" element={
            <ProtectedRoute><Training /></ProtectedRoute>
          } />
          <Route path="/volunteer" element={
            <ProtectedRoute><Volunteer /></ProtectedRoute>
          } />
          <Route path="/faqs" element={
            <ProtectedRoute><FAQs /></ProtectedRoute>
          } />
          <Route path="/admin" element={
            <ProtectedRoute><AdminPanel /></ProtectedRoute>
          } />
          <Route path="/pathway/*" element={
            <ProtectedRoute><Learn /></ProtectedRoute>
          } />
          <Route path="/privacy" element={
            <ProtectedRoute><About /></ProtectedRoute>
          } />
          <Route path="/terms" element={
            <ProtectedRoute><About /></ProtectedRoute>
          } />
          <Route path="/profile" element={
            <ProtectedRoute><Dashboard /></ProtectedRoute>
          } />
          <Route path="/favorites" element={
            <ProtectedRoute><Resources /></ProtectedRoute>
          } />
          
          {/* Catch-all redirect */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </main>
      <Footer />
    </div>
  );
};

function App() {
  return (
    <SensoryProvider>
      <AuthProvider>
        <BrowserRouter>
          <AppRoutes />
          <Toaster position="top-right" richColors />
        </BrowserRouter>
      </AuthProvider>
    </SensoryProvider>
  );
}

export default App;
